import {
  BodyText,
  Button,
  Panel,
  SkeletonLoader,
} from '@postscript/components';
import {
  useApproveProposal,
  useOfferProposal,
  useRejectProposal,
} from 'components/brand/offers/hooks/useOfferProposals';
import {
  ProposalDecisionActions,
  ProposalDetail,
} from 'components/brand/offers/OfferProposalDetail';
import { ProposalEditForm } from 'components/brand/offers/ProposalEditForm';
import {
  OfferProposal,
  ProposalDisposition,
} from 'components/brand/offers/types';
import { Ref, useRef, useState } from 'react';
import styled from 'styled-components';

const ReviewBody = styled.div`
  display: grid;
  align-content: start;
  gap: var(--spacing-3);
  min-height: min(36rem, calc(100vh - 14rem));
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-2);
  width: 100%;
  flex-wrap: wrap;

  > div {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-1);
  }
`;

const REVIEW_TITLES: Partial<Record<ProposalDisposition, string>> = {
  [ProposalDisposition.NEW]: 'Create draft offer',
  [ProposalDisposition.ENRICH]: 'Review changes to an existing offer',
  [ProposalDisposition.POSSIBLE_MATCH]: 'Review possible match',
  [ProposalDisposition.CONFLICTED]: 'Review conflicting terms',
};

export const PROPOSAL_REVIEW_PANEL_STYLE = {
  width: 'min(880px, calc(100vw - 48px))',
  height: 'min(960px, calc(100vh - 48px))',
};

interface ProposalReviewSectionsOptions {
  proposal: OfferProposal | null;
  sightingCount: number;
  onClose: () => void;
  /** Runs after an approve, link or dismiss succeeds. Defaults to onClose. */
  onDecided?: () => void;
  /** Shows the header back button; Cancel then goes back instead of closing. */
  onBack?: () => void;
  headerRef?: Ref<HTMLElement>;
}

export interface ProposalReviewSectionElements {
  header: JSX.Element;
  body: JSX.Element;
  footer: JSX.Element | null;
}

/**
 * Returns the review's Panel sections as separate elements because Panel
 * only pins a header and footer that are its direct children. The review
 * state resets whenever the proposal id changes, so one host Panel can review
 * several proposals in turn.
 */
export const useProposalReviewSections = ({
  proposal,
  sightingCount,
  onClose,
  onDecided = onClose,
  onBack,
  headerRef,
}: ProposalReviewSectionsOptions): ProposalReviewSectionElements | null => {
  const proposalId = proposal?.id ?? null;
  // A decision can settle after the merchant moved on to another offer;
  // only the review it was made for may navigate away.
  const activeIdRef = useRef(proposalId);
  activeIdRef.current = proposalId;
  const decidedFor = (id: number) => () => {
    if (activeIdRef.current === id) onDecided();
  };
  const startsEditing = proposal?.disposition === ProposalDisposition.NEW;
  const [reviewedId, setReviewedId] = useState(proposalId);
  const [editing, setEditing] = useState(startsEditing);
  const [canSubmitForm, setCanSubmitForm] = useState(true);
  if (reviewedId !== proposalId) {
    setReviewedId(proposalId);
    setEditing(startsEditing);
    setCanSubmitForm(true);
  }

  const approve = useApproveProposal();
  const reject = useRejectProposal();
  const detailQuery = useOfferProposal(proposal?.id ?? null);
  if (!proposal) return null;

  const formId = `proposal-review-form-${proposal.id}`;
  const currentProposal = detailQuery.data
    ? { ...proposal, ...detailQuery.data }
    : proposal;
  const submitLabel =
    currentProposal.disposition === ProposalDisposition.NEW
      ? 'Create draft offer'
      : 'Save changes';

  const header = (
    <Panel.Header
      key={`header-${proposal.id}`}
      ref={headerRef}
      tabIndex={headerRef ? -1 : undefined}
      onCancel={onClose}
      showBackButton={Boolean(onBack)}
      onBack={onBack}
      description={
        currentProposal.targetKind === 'oms_offer'
          ? 'Confirm whether the storefront evidence matches this offer. The existing offer stays unchanged.'
          : 'Check the storefront evidence and the result before making a decision.'
      }
    >
      {currentProposal.targetKind === 'oms_offer'
        ? 'Confirm an existing offer match'
        : REVIEW_TITLES[proposal.disposition] ?? 'Review scanned offer'}
    </Panel.Header>
  );

  const body = (
    <Panel.Body key={`body-${proposal.id}`}>
      <ReviewBody>
        <BodyText bold>
          {currentProposal.consolidatedOfferName ?? currentProposal.offerName}
        </BodyText>
        {detailQuery.isError ? (
          <BodyText color="var(--error-text-color)">
            This offer could not be refreshed. Close this review and try again.
          </BodyText>
        ) : detailQuery.isLoading && !detailQuery.data ? (
          <SkeletonLoader width="full" height={360} borderRadius="medium" />
        ) : editing ? (
          <ProposalEditForm
            proposal={currentProposal}
            onDone={() => setEditing(false)}
            formId={formId}
            hideActions
            onCanSubmitChange={setCanSubmitForm}
            submitLabel={submitLabel}
            onSaved={
              currentProposal.disposition === ProposalDisposition.NEW
                ? (updatedProposal) =>
                    approve.mutate(
                      {
                        id: updatedProposal.id,
                        disposition: updatedProposal.disposition,
                        expectedProposalUpdatedAt: updatedProposal.updatedAt,
                      },
                      { onSuccess: decidedFor(updatedProposal.id) },
                    )
                : undefined
            }
          />
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={() => setEditing(true)}
            >
              Edit scanned details
            </Button>
            <ProposalDetail
              proposal={currentProposal}
              sightingCount={sightingCount}
              showActions={false}
            />
          </>
        )}
      </ReviewBody>
    </Panel.Body>
  );

  const footer = detailQuery.isError ? null : (
    <Panel.Footer key={`footer-${proposal.id}`}>
      {detailQuery.isLoading ? (
        <SkeletonLoader width="full" height={36} borderRadius="medium" />
      ) : (
        <FooterActions>
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={onBack ?? onClose}
            >
              Cancel
            </Button>
            {editing && (
              <Button
                type="button"
                variant="text"
                loading={reject.isLoading}
                disabled={reject.isLoading || approve.isLoading}
                onClick={() =>
                  reject.mutate(currentProposal.id, {
                    onSuccess: decidedFor(currentProposal.id),
                  })
                }
              >
                Dismiss offer
              </Button>
            )}
          </div>
          {editing ? (
            <Button
              type="submit"
              form={formId}
              loading={approve.isLoading}
              disabled={!canSubmitForm || approve.isLoading || reject.isLoading}
            >
              {submitLabel}
            </Button>
          ) : (
            <ProposalDecisionActions
              proposal={currentProposal}
              onDecided={decidedFor(currentProposal.id)}
            />
          )}
        </FooterActions>
      )}
    </Panel.Footer>
  );

  return { header, body, footer };
};
